/* eslint-disable max-classes-per-file */
import { NgStyle } from "@angular/common";
import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { LabelComponent } from "@components/label/label.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ColorPickerModalComponent } from "~app/common/components/color-picker/color-picker-modal/color-picker-modal.component";
import { ToastService } from "~app/common/components/toast/toast.service";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { KundDetailsModalTexts } from "~app/features/forsaljning/kund/components/kund-details-modal/kund-details-modal.texts";
import { IKundDetail } from "~app/features/forsaljning/kund/models/kundDetail";
import { KundService } from "~app/features/forsaljning/kund/services/kund.service";
import { KundDetailsModalBefraktarstyrningComponent } from "./befraktarstyrning/kund-details-modal-befraktarstyrning.component";

@Component({
    selector: "rask-kund-details-modal",
    templateUrl: "./kund-details-modal.component.html",
    styleUrls: ["./kund-details-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TextmultilineComponent,
        CheckboxComponent,
        FormsModule,
        SelectorComponent,
        TextareaComponent,
        NgStyle,
        LabelComponent,
        ReactiveFormsModule,
        KundDetailsModalBefraktarstyrningComponent,
        ActionButtonComponent,
        SaveButtonComponent
    ]
})
export class KundDetailsModalComponent implements OnInit {

    header: string;
    data: IKundDetail;
    texts = KundDetailsModalTexts.texts;
    kund: KundDetailsViewModel;
    dataChanged = false;
    canEdit = false;
    form: UntypedFormGroup;
    initialized = false;
    colors: string[];
    activeModal = inject(NgbActiveModal);

    private readonly kundService = inject(KundService);
    private readonly busyService = inject(BusyService);
    private readonly toasterService = inject(ToastService);
    private readonly dirtyForm = inject(DirtyFormService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly ngbModal = inject(NgbModal);

    get koppladeKunder() {
        return this.kund.koppladeKunder.map(x => `${x.huvudkod} - ${x.internnummer}`).join(", ");
    }

    ngOnInit(): void {
        this.formatData();
        this.form = new UntypedFormGroup({});
        this.canEdit = this.authenticationService.canEdit();

        this.dirtyForm.onDirtyNgModel(
            this.isModalDirty.bind(this),
            this.save.bind(this),
            d => this.dirtyCheckCallback(d),
            () => this.canSave(),
            this.texts.leaveHeader,
            this.texts.leaveBody,
            { size: "lg",
                centered: false });

        this.initialized = true;
        this.loadColors();
    }

    disableDoljkund(): boolean {
        return this.kund.koppladeKunder.length !== 0 || this.kund.koppladTillKundorder;
    }

    dismiss() {
        if (this.dirtyForm.canClose()) {
            this.activeModal.dismiss();
        }
    }

    kundFormatter = item => item ? `${item.huvudkod} - ${item.internnummer}` : undefined;

    idFormatter = (value: IKundDetail) => value ? value.id : undefined;

    dirtyCheckCallback(result: IDirtyFormResult): void {
        if (result.isSkipSave) {
            this.activeModal.dismiss();
        }
        if (result.isSave) {
            this.activeModal.dismiss();
        }
    }

    isModalDirty(): boolean {
        return this.dataChanged;
    }

    formatData() {
        this.kund = {
            uppdaterad: this.data.receiveDateTime ? DateUtils.formatDateString(this.data.receiveDateTime, DateUtils.datetimeformat) : "",
            statusILime: (this.data && this.data?.aktivLime) ? this.texts.aktivILime : this.texts.inaktivILime,
            besoksadress: [
                this.data.besoksadress,
                this.data.besoksadress2,
                this.data.besokPostnummer,
                this.data.besokPostortadress
            ],
            postadress: [
                this.data.adress,
                this.data.adress2,
                this.data.postnummer,
                this.data.postort
            ],
            avtalsansvarig: this.data.avtalsansvarig,
            avdelning: this.data.avdelning,
            limeId: this.data.limeId,
            huvudkod: this.data.huvudkod,
            internnummer: this.data.internnummer,
            telefon: this.data.telefon,
            orgNummer: this.data.orgNummer,
            landOchLandskod: `${this.data.land} ${this.data.landISO}`,
            doljForKundorder: this.data.doljForKundorder,
            valbaraForaldraKunder: this.data.valbaraForaldraKunder,
            koppladeKunder: this.data.koppladeKunder,
            koppladTillKundorder: this.data.koppladTillKundorder
        };
    }

    save() {
        if (!this.canSave()) {
            return;
        }
        this.busyService.addWork(this.kundService.saveKund(this.data))
            .subscribe(res => {
                if (res) {
                    this.data = res;
                    this.formatData();
                    this.toasterService.add("DEFAULT_CHANGE_SUCCESS");
                    this.dataChanged = false;
                }
            });

        this.activeModal.dismiss();
    }

    onChange() {
        this.dataChanged = true;
    }

    onDoldChange(value: any) {
        this.dataChanged = true;
        if(!value) {
            this.data.parentKundId = undefined;
        }
    }

    onBefraktareChange() {
        this.onChange();

        if (!this.data.befraktare) {
            this.data.befraktarstyrningar.splice(0, this.data.befraktarstyrningar.length);
        }
    }

    onDeleteRow($event) {
        this.data.befraktarstyrningar.splice($event, 1);
        this.dataChanged = true;
    }

    canSave() {
        if (this.form.dirty || this.dataChanged) {
            // eslint-disable-next-line sonarjs/prefer-single-boolean-return
            if (this.form.dirty && !this.form.valid) {
                return false;
            }
            return true;
        } else {
            return false;
        }
    }

    addBefraktarstyrning() {
        this.data.befraktarstyrningar.push({
            handelssortimentId: null,
            marknadsomraden: [],
            resultatomraden: [],
            produktionsomraden: []
        });
    }

    loadColors() {
        this.kundService.getBefraktareColors().subscribe(colors => {
            this.colors = colors;
        });
        if(!this.data.color) {
            this.data.color = this.data.defaultColor;
            this.form.markAsDirty();
        }
    }

    openColorPicker() {
        const modalRef = this.ngbModal.open(ColorPickerModalComponent, { size: "sm",
            backdrop: "static" });
        modalRef.componentInstance.color = this.data.color;
        modalRef.componentInstance.colors = this.colors;
        modalRef.result.then(color => {
            if (color) {
                this.data.color = color;
                this.form.markAsDirty();
            }
        });
    }
}

export class KundDetailsViewModel {
    uppdaterad: string;
    besoksadress: string[];
    statusILime: string;
    postadress: string[];
    avtalsansvarig: string;
    avdelning: string;
    limeId: string;
    huvudkod: string;
    internnummer: string;
    telefon: string;
    orgNummer: string;
    landOchLandskod: string;
    doljForKundorder: boolean;
    valbaraForaldraKunder: IKundDetail[];
    koppladeKunder: IKundDetail[];
    koppladTillKundorder: boolean;
}
